import React, { lazy, Suspense, useContext, useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../Context/AuthContext";
import LoginPage from "../Pages/LoginPage/LoginPage";
import NotFound from "../Pages/NotFoundPage/NotFound";
import { CustomLoader } from "../CommonComponents/loader/Loader";
import PartnerManagement from "../Pages/partnerManagement/PartnerManagement";
import AllPartner from "../Pages/allPartner/AllPartner";

// Customer Pages
const Dashboard = lazy(() => import("../Pages/Dashboard/Dashboard"));
const OrderTrackingPage = lazy(() =>
  import("../Pages/OrderTrackingPage/OrderTrackingPage")
);
const OrderPage = lazy(() => import("../Pages/OrderPage/OrderPage"));
const OrderDetailPage = lazy(() =>
  import("../Pages/OrderDetailPage/OrderDetailPage")
);
const CartPage = lazy(() => import("../Pages/CartPage/CartPage"));
const AddressPage = lazy(() => import("../Pages/AddressPage/AddressPage"));
const AddressFormPage = lazy(() =>
  import("../Pages/AddressFormPage/AddressFormPage")
);
const WalletPage = lazy(() => import("../Pages/WalletPage/WalletPage"));
const OrderSuccessPage = lazy(() =>
  import("../Pages/OrderSuccessPage/OrderSuccessPage")
);
const OrderFailurePage = lazy(() =>
  import("../Pages/OrderFailurePage/OrderFailurePage")
);
const Policy = lazy(() => import("../Pages/Policy/Policy"));
const Contact = lazy(() => import("../Pages/Contact/Contact"));

// Store Pages
const AdminDashboard = lazy(() =>
  import("../Pages/adminDashboard/AdminDashBoard")
);
const OrderManagement = lazy(() =>
  import("../Pages/orderManagement/OrderManagement")
);
const KitchenManagement = lazy(() =>
  import("../Pages/kitchenManagement/KitchenManagement")
);
const ComplaintManagement = lazy(() =>
  import("../Pages/complaintManagement/ComplaintManagement")
);

const TallyAtOnce = lazy(() => 
import("../Pages/TallyAtOnce/TallyAtOnce")
);

const ActiveOrders = lazy(() => import("../Pages/activeOrders/ActiveOrders"));
const AllOrders = lazy(() => import("../Pages/allOrders/AllOrders"));
const StockManagement = lazy(() => import("../Pages/stockManagement/StockManagement"));
const ItemMaster = lazy(() => import("../Pages/itemMaster/ItemMaster"));
const Keywords = lazy(() => import("../Pages/keywords/Keywords"));
const AllIssues = lazy(() => import("../Pages/allIssues/AllIssues"));
const CartStaffManagement = lazy(() =>
  import("../Pages/cartStaffManagement/CartStaffManagement")
);
const EmployeeManagement = lazy(() =>
  import("../Pages/employeeManagement/EmployeeManagement")
);
const QueryDataPage = lazy(() => import("../Pages/ai-tools/QueryDataPage"));
const SSEDebugger = lazy(() => import("../Components/ai-tools/SSEDebugger"));
const NotificationsPage = lazy(() => import("../Pages/NotificationsPage/NotificationsPage"));

const PosDashboard = lazy(() => import("../Pages/pos/PosDashboard"));

const FranchiseControlCenter = lazy(() => import("../Pages/franchiseSupplyNetwork/franchiseControlCenter/FranchiseControlCenter"));
const Overview = lazy(() => import("../Pages/franchiseSupplyNetwork/overview/Overview"));
const Outlets = lazy(() => import("../Pages/franchiseSupplyNetwork/outlets/Outlets"));
const OutletDeepDriveRedirect = lazy(() => import("../Pages/franchiseSupplyNetwork/outletDeepDrive/OutletDeepDriveRedirect"));
const OutletDeepDrive = lazy(() => import("../Pages/franchiseSupplyNetwork/outletDeepDrive/OutletDeepDrive"));
const Alerts = lazy(() => import("../Pages/franchiseSupplyNetwork/alerts/Alerts"));
const Leaderboard = lazy(() => import("../Pages/franchiseSupplyNetwork/leaderboard/Leaderboard"));
const Settings = lazy(() => import("../Pages/franchiseSupplyNetwork/settings/Settings"));

// Private Route component
const PrivateRoute = ({ children, allowedPartnerTypes }) => {
  const { authToken, partnerType } = useContext(AuthContext);

  if (!authToken) return <Navigate to="/login" replace />;
  // if (!allowedPartnerTypes.includes(partnerType))
  //   return <Navigate to="/not-found" replace />;
  return children;
};

// Main Router component
const Routers = () => {
  const location = useLocation();
  const { authToken, partnerType } = useContext(AuthContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <Suspense fallback={<CustomLoader />}>
      <Routes>
        <Route path="login" element={<LoginPage />} />
        {/* Customer Routes */}
        {[
          { path: "/customer-dashboard", element: <Dashboard /> },
          { path: "/orders", element: <OrderTrackingPage /> },
          { path: "/order-page", element: <OrderPage /> },
          { path: "/order-detail/:orderId", element: <OrderDetailPage /> },
          { path: "/cart-page", element: <CartPage /> },
          { path: "/address-page", element: <AddressPage /> },
          { path: "/add-address-page", element: <AddressFormPage /> },
          { path: "/wallet-page", element: <WalletPage /> },
          { path: "/order-success", element: <OrderSuccessPage /> },
          { path: "/order-failure", element: <OrderFailurePage /> },
          { path: "/policy", element: <Policy /> },
          { path: "/contactUs", element: <Contact /> },
        ].map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={
              <PrivateRoute allowedPartnerTypes={["customer", "admin"]}>
                {element}
              </PrivateRoute>
            }
          />
        ))}

        {/* Store Routes */}
        <Route
          path="/admin-dashboard"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
         <Route
          path="/all-orders-tally"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <TallyAtOnce />
            </PrivateRoute>
          }
        />
        <Route
          path="/order-management"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <OrderManagement />
            </PrivateRoute>
          }
        >
          {/* Nested Routes for Active and All Orders */}
          <Route path="active-orders" element={<ActiveOrders />} />
          <Route path="all-orders" element={<AllOrders />} />
        </Route>

        <Route
          path="/kitchen-management"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <KitchenManagement />
            </PrivateRoute>
          }
        >
          <Route path="stock-management" element={<StockManagement />} />
          <Route path="item-master" element={<ItemMaster />} />
          <Route path="keywords" element={<Keywords />} />
        </Route>

        <Route
          path="/partner-management"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <PartnerManagement />
            </PrivateRoute>
          }
        >
          <Route path="all-partner" element={<AllPartner />} />
        </Route>

        <Route
          path="/cart-staff-management"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <CartStaffManagement />
            </PrivateRoute>
          }
        >
          <Route path="employee-management" element={<EmployeeManagement />} />
        </Route>

        <Route
          path="/complaint-management"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <ComplaintManagement />
            </PrivateRoute>
          }
        >
          <Route path="all-issues" element={<AllIssues />} />
        </Route>

        <Route
          path="/ai-tools/query-data"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <QueryDataPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/ai-tools/debug"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <SSEDebugger />
            </PrivateRoute>
          }
        />

        <Route
          path="/pos-dashboard"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <PosDashboard />
            </PrivateRoute>
          }
        />

        {/* Notifications Page - accessible by all authenticated users */}
        <Route
          path="/notifications"
          element={
            <PrivateRoute allowedPartnerTypes={["admin", "customer"]}>
              <NotificationsPage />
            </PrivateRoute>
          }
        />

         <Route
          path="/franchise-control-center"
          element={
            <PrivateRoute allowedPartnerTypes={["admin"]}>
              <FranchiseControlCenter />
            </PrivateRoute>
          }
        >
          <Route path="overview" element={<Overview />} />
          <Route path="outlets" element={<Outlets />} />
          <Route
    path="outlet-deep-drive"
    element={<OutletDeepDriveRedirect />}
  />
            <Route
        path="outlet-deep-drive/:outletName"
        element={<OutletDeepDrive />}
      />
          {/* <Route path="alerts" element={<Alerts />} /> */}
           <Route path="leaderboard" element={<Leaderboard />} />
          {/* <Route path="settings" element={<Settings />} /> */}
        </Route>

        {/* Redirect based on partner type */}
        <Route
          path="/"
          element={
            authToken ? (
              partnerType === "customer" ? (
                <Navigate to="/customer-dashboard" />
              ) : (
                <Navigate to="/admin-dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/not-found" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/not-found" />} />
      </Routes>
    </Suspense>
  );
};

export default Routers;